/**
 * Workshop Form Modal
 * Modal for creating and editing workshops with database configuration
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Database,
  Save,
  AlertCircle,
  CheckCircle,
  Loader,
} from "lucide-react";
import { Workshop } from "../../types/workshop";
import { useSnackbar } from "notistack";
import api from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

interface WorkshopFormModalProps {
  workshop?: Workshop | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

interface WorkshopFormData {
  name: string;
  code: string;
  location: string;
  db_host: string;
  db_port: number;
  db_user: string;
  db_password: string;
  db_name: string;
  description: string;
  is_active: boolean;
}

const WorkshopFormModal: React.FC<WorkshopFormModalProps> = ({
  workshop,
  isOpen,
  onClose,
  onSave,
}) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState<WorkshopFormData>({
    name: "",
    code: "",
    location: "",
    db_host: "localhost",
    db_port: 3306,
    db_user: "",
    db_password: "",
    db_name: "",
    description: "",
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<"success" | "error" | null>(
    null
  );
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (isOpen && workshop) {
      // Edit mode - populate form
      setFormData({
        name: workshop.name,
        code: workshop.code,
        location: workshop.location || "",
        db_host: workshop.db_host,
        db_port: workshop.db_port,
        db_user: workshop.db_user,
        db_password: workshop.db_password,
        db_name: workshop.db_name,
        description: workshop.description || "",
        is_active: workshop.is_active,
      });
    } else if (isOpen) {
      // Create mode - reset form
      setFormData({
        name: "",
        code: "",
        location: "",
        db_host: "localhost",
        db_port: 3306,
        db_user: "",
        db_password: "",
        db_name: "",
        description: "",
        is_active: true,
      });
    }
    setTestResult(null);
  }, [isOpen, workshop]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value) : value,
    }));
    setTestResult(null);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);

      // TODO: Implement test connection endpoint
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setTestResult("success");
      enqueueSnackbar("Database connection successful!", {
        variant: "success",
      });
    } catch (error) {
      setTestResult("error");
      enqueueSnackbar("Database connection failed", { variant: "error" });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.code || !formData.db_name) {
      enqueueSnackbar("Please fill in all required fields", {
        variant: "warning",
      });
      return;
    }

    if (!formData.db_host || !formData.db_user) {
      enqueueSnackbar("Please fill in database host and user", {
        variant: "warning",
      });
      return;
    }

    try {
      setSaving(true);

      if (workshop) {
        // Update existing workshop
        await api.put(`/workshops/${workshop.id}`, formData);
        enqueueSnackbar("Workshop updated successfully", {
          variant: "success",
        });
      } else {
        // Create new workshop
        await api.post("/workshops/", formData);
        enqueueSnackbar("Workshop created successfully", {
          variant: "success",
        });
      }

      onSave();
      onClose();
    } catch (error: any) {
      const message = error.response?.data?.detail || "Error saving workshop";
      enqueueSnackbar(message, { variant: "error" });
      console.error("Workshop save error:", error.response?.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className={`relative w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-phoenix-600 to-phoenix-700 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Database className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">
                      {workshop ? "Edit Workshop" : "Create New Workshop"}
                    </h2>
                    <p className="text-phoenix-100 text-sm mt-1">
                      Configure workshop database connection
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="flex-1 overflow-y-auto p-6"
            >
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Workshop Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                                 rounded-lg focus:ring-2 focus:ring-phoenix-500 focus:border-transparent
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Phoenix Odessa Workshop"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Workshop Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        required
                        disabled={!!workshop}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                                 rounded-lg focus:ring-2 focus:ring-phoenix-500 focus:border-transparent
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                 disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed"
                        placeholder="PHX_ODESSA"
                      />
                      {workshop && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Code cannot be changed after creation
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                                 rounded-lg focus:ring-2 focus:ring-phoenix-500 focus:border-transparent
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Odessa, TX"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Status
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleCheckboxChange}
                          className="w-4 h-4 text-phoenix-600 border-gray-300 rounded focus:ring-phoenix-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Workshop is active
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                               rounded-lg focus:ring-2 focus:ring-phoenix-500 focus:border-transparent
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Optional notes about this workshop..."
                    />
                  </div>
                </div>

                {/* Database Configuration */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Database Configuration
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Host <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="db_host"
                        value={formData.db_host}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                                 rounded-lg focus:ring-2 focus:ring-phoenix-500 focus:border-transparent
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="localhost"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Port <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="db_port"
                        value={formData.db_port}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                                 rounded-lg focus:ring-2 focus:ring-phoenix-500 focus:border-transparent
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="3306"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Database Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="db_name"
                        value={formData.db_name}
                        onChange={handleChange}
                        required
                        disabled={!!workshop}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                                 rounded-lg focus:ring-2 focus:ring-phoenix-500 focus:border-transparent
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                 disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed"
                        placeholder="phoenix_workshop"
                      />
                      {workshop && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Database name cannot be changed after creation
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Username <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="db_user"
                        value={formData.db_user}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                                 rounded-lg focus:ring-2 focus:ring-phoenix-500 focus:border-transparent
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="workshop_user"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Password{" "}
                        <span className="text-gray-400 text-xs">
                          (leave empty for no password)
                        </span>
                      </label>
                      <input
                        type="password"
                        name="db_password"
                        value={formData.db_password}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                                 rounded-lg focus:ring-2 focus:ring-phoenix-500 focus:border-transparent
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Leave empty for no password (e.g., root without password)"
                      />
                    </div>
                  </div>

                  {/* Test Connection Button */}
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handleTestConnection}
                      disabled={
                        testing || !formData.db_name || !formData.db_user
                      }
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 
                               text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 
                               transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {testing ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Testing Connection...
                        </>
                      ) : testResult === "success" ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Connection Successful
                        </>
                      ) : testResult === "error" ? (
                        <>
                          <AlertCircle className="w-4 h-4 text-red-600" />
                          Connection Failed
                        </>
                      ) : (
                        <>
                          <Database className="w-4 h-4" />
                          Test Connection
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>

            {/* Footer */}
            <div
              className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-t border-gray-200 
                          dark:border-gray-700 flex justify-end gap-3"
            >
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 
                         text-gray-700 dark:text-gray-300 rounded-lg
                         hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-phoenix-600 text-white rounded-lg
                         hover:bg-phoenix-700 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {workshop ? "Update Workshop" : "Create Workshop"}
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default WorkshopFormModal;
